import {useRef, useState} from 'react';
import {useFormContext} from 'react-hook-form';
import AppMenu from '../../../../../../../../components/common/AppMenu';
import {SVG} from '../../../../../../../../constants/assetLists';
import {useSnackbar} from '../../../../../../../../context/SnackbarContextProvider';
import {useOnBoardingContext} from '../../../../../../../../context/onBoarding/OnBoardingContextProvider';
import {
  DepartmentFormValues,
  NetworkDetailsFormValues,
  NetworkNodeFieldPath,
  OnboardingUserFormValues,
  RegulationFormValues,
  RoleFormValues,
} from '../../../../types';
import DuplicateRegulationDialog from '../DuplicateRegulationDialog';
import {
  downloadOnboardingRegulationExcelExport,
  readOnboardingRegulationExcelFile,
} from '../regulationExcelTemplate';

const MENU_ITEMS = [
  {
    title: 'שכפול',
    value: 'duplicate',
    icon: SVG.copyGrayIcon,
  },
  {
    title: 'יבוא',
    value: 'import',
    icon: SVG.excelGreenIcon,
  },
  {
    title: 'יצוא',
    value: 'export',
    icon: SVG.excelGreenIcon,
  },
];

type Props = {
  selectedFieldPath: NetworkNodeFieldPath;
  globalIndex: number;
  selectedFolderId: string | null;
  onSaved?: () => void;
};

function RegulationRowMenu({
  selectedFieldPath,
  globalIndex,
  selectedFolderId,
  onSaved,
}: Props) {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [showDuplicateDialog, setShowDuplicateDialog] = useState(false);
  const [duplicatingRegulationId, setDuplicatingRegulationId] = useState<
    string | null
  >(null);

  const excelInputRef = useRef<HTMLInputElement>(null);
  const {showSnackbar} = useSnackbar();
  const {allRoleTypes} = useOnBoardingContext();
  const {getValues, setValue} = useFormContext<NetworkDetailsFormValues>();

  const regulationsPath = `${selectedFieldPath}.regulations` as const;

  const handleMenuOpen = (e: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(e.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleDuplicateDialogClose = () => {
    setShowDuplicateDialog(false);
    setDuplicatingRegulationId(null);
  };

  const handleImportClick = () => {
    if (!selectedFolderId) return;
    excelInputRef.current?.click();
  };

  const handleItemClick = (value: string) => {
    const regulations: RegulationFormValues[] =
      getValues(regulationsPath) ?? [];
    const regulation = regulations[globalIndex];

    if (value === 'duplicate' && regulation) {
      setDuplicatingRegulationId(regulation.id);
      setShowDuplicateDialog(true);
    } else if (value === 'import') {
      handleImportClick();
    } else if (value === 'export' && regulation) {
      const nodeDepartments =
        (getValues(
          `${selectedFieldPath}.departments`,
        ) as DepartmentFormValues[]) ?? [];
      const nodeUsers =
        (getValues(`${selectedFieldPath}.users`) as OnboardingUserFormValues[]) ??
        [];

      downloadOnboardingRegulationExcelExport(
        regulation,
        nodeDepartments,
        nodeUsers,
      );
    }

    handleMenuClose();
  };

  const handleExcelPicked: React.ChangeEventHandler<
    HTMLInputElement
  > = async e => {
    const file = e.target.files?.[0];
    if (!file || !selectedFolderId) return;

    const nodeDepartments =
      (getValues(
        `${selectedFieldPath}.departments`,
      ) as DepartmentFormValues[]) ?? [];
    const nodeUsers =
      (getValues(`${selectedFieldPath}.users`) as OnboardingUserFormValues[]) ??
      [];
    const nodeRoles =
      (getValues(`${selectedFieldPath}.roles`) as RoleFormValues[]) ?? [];

    try {
      const {regulations: imported, rowErrors} =
        await readOnboardingRegulationExcelFile(file, {
          nodeDepartments,
          nodeUsers,
          nodeRoles,
          allRoleTypes,
          folderId: selectedFolderId,
        });

      if (imported.length === 0) {
        const detail =
          rowErrors.length > 0
            ? rowErrors.slice(0, 3).join(' | ')
            : 'לא נמצאו רגולציות תקינות בקובץ';
        showSnackbar({
          type: 'error',
          message: detail,
        });
        return;
      }

      const currentRegulations = getValues(regulationsPath) ?? [];
      setValue(regulationsPath, [...currentRegulations, ...imported], {
        shouldValidate: true,
        shouldDirty: true,
      });

      const successMessage =
        rowErrors.length > 0
          ? `${imported.length} רגולציות יובאו. ${rowErrors.length} שורות נדחו.`
          : `${imported.length} רגולציות יובאו בהצלחה`;

      showSnackbar({
        type: rowErrors.length > 0 ? 'info' : 'success',
        message: successMessage,
      });

      onSaved?.();

      if (rowErrors.length > 0) {
        console.warn('Regulation import row errors:', rowErrors);
      }
    } catch (err) {
      console.error(err);
      showSnackbar({
        type: 'error',
        message: 'שגיאה בקריאת קובץ האקסל',
      });
    } finally {
      e.target.value = '';
    }
  };

  return (
    <>
      <AppMenu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onOpen={handleMenuOpen}
        onClose={handleMenuClose}
        items={MENU_ITEMS}
        onItemClick={handleItemClick}
      />

      <input
        ref={excelInputRef}
        type="file"
        accept=".xlsx,.xls"
        className="hidden"
        onChange={handleExcelPicked}
      />

      <DuplicateRegulationDialog
        open={showDuplicateDialog}
        onClose={handleDuplicateDialogClose}
        onSaved={() => {
          onSaved?.();
          handleDuplicateDialogClose();
        }}
        selectedFieldPath={selectedFieldPath}
        editingRegulationId={duplicatingRegulationId ?? undefined}
        selectedFolderId={selectedFolderId}
      />
    </>
  );
}

export default RegulationRowMenu;
